"use client";

import type { Interpolation } from "@emotion/react";
import { I18nMessage, useTranslations } from "@i18n/react";
import { useRouter } from "@packages/framework";
import type { useRequestQuery } from "@packages/sdk";
import { Button, useDialog } from "@packages/ui";
import { useEffect, useState } from "react";

import { ProfilesSwitch } from "../components/ProfilesSwitch";

enum RestrictedContentErrors {
  Collection = 10003,
  Prayer = 10004,
  Page = 10005,
}

const restrictedPopup: Interpolation = {
  maxWidth: 480,
};

const OKButton = (_, modal) => (
  <Button
    variant="primary"
    isFullWidth
    size="l"
    onClick={() => {
      modal.resolve(false);
      modal.remove();
    }}
  >
    <I18nMessage message="general_word_ok" />
  </Button>
);

const SwitchButton = (_, modal) => (
  <Button asChild variant="secondary" size="l" isFullWidth>
    <ProfilesSwitch
      onClick={() => {
        modal.resolve(true);
        modal.hide(); // NOT remove so the tapped behavior continues to work
      }}
      switchProfilesProps={{
        onSuccess: () => location.reload(),
      }}
    />
  </Button>
);

export const useRestrictedContentCheck = ({
  query,
}: {
  query: ReturnType<typeof useRequestQuery>;
}) => {
  const t = useTranslations();
  const router = useRouter();
  const confirm = useDialog({
    buttons: [OKButton, SwitchButton],
    buttonFormat: "vertical",
    showCloseButton: true,
    title: () => t("access_restricted_alert_title"),
    description: () => t("access_restricted_alert_message"),
    modalProps: { contentCSS: restrictedPopup },
    onOpenChange: (open) => {
      if (open) return confirm.show();
      confirm.resolve(false);
      confirm.remove();
    },
  });
  const [isRestricted, setIsRestricted] = useState<boolean>(false);

  useEffect(() => {
    const doPopup = async () => {
      const result = (await confirm.show()) as boolean;
      if (!result) {
        return router.push("/home");
      }
    };

    if (
      query.isError &&
      [
        RestrictedContentErrors.Collection,
        RestrictedContentErrors.Prayer,
        RestrictedContentErrors.Page,
      ].includes(query.error.code)
    ) {
      setIsRestricted(true);
      doPopup();
    }
  }, [query.isError, query.error]);

  return isRestricted;
};
